'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';
import useInstallPrompt from '@alltrails/install-prompt/hooks/useInstallPrompt';
import type { TrailId } from '@alltrails/types/TrailId';
import ScrollLimit from '@alltrails/user-auth/components/ScrollLimit/ScrollLimit';

const MiniInstallInterstitial = dynamic(() => import('@alltrails/install-prompt/components/MiniInstallInterstitial'), { ssr: false });

type Props = {
  hasReachedPageLimit: boolean;
  trailId: TrailId;
};

export default function MiniInterstitialTreatment({ hasReachedPageLimit, trailId }: Props) {
  const [isOpen, setIsOpenAttempt] = useInstallPrompt('interstitial', false);
  // Once the interstitial has been attempted, never re-trigger it regardless of further scrolling.
  const hasFiredRef = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      if (hasFiredRef.current) {
        return;
      }
      if (window.scrollY >= window.innerHeight) {
        hasFiredRef.current = true;
        setIsOpenAttempt(true);
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [setIsOpenAttempt]);

  if (hasReachedPageLimit) {
    return <ScrollLimit deeplinkData={{ pageId: trailId, pageType: 'trail' }} />;
  }

  return (
    <>
      {isOpen && (
        <MiniInstallInterstitial
          clickOutsideDeactivates={false}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          isOpen={isOpen}
          onRequestClose={() => setIsOpenAttempt(false)}
          promptLocation="trail page"
          variant="app-info"
        />
      )}
    </>
  );
}
